'use client';

/**
 * Full-bleed one-slide-at-a-time hero with horizontal slide / fade
 * transitions. Ported from ThemeMaker's
 * {@code blocks/hero/HeroCarousel}.
 *
 * <h3>Behaviour</h3>
 * <ul>
 *   <li><b>1 slide</b> — static hero, no controls, no autoplay.</li>
 *   <li><b>2+ slides</b> — carousel with prev/next arrows, dots,
 *       optional autoplay.</li>
 *   <li>Touch swipe (50px threshold) — works on mobile.</li>
 *   <li>Keyboard: Left/Right arrows + Home/End on a focused carousel.</li>
 *   <li>ARIA: {@code role="region"} with
 *       {@code aria-roledescription="carousel"}; each slide is
 *       {@code role="group"} with {@code aria-roledescription="slide"};
 *       a polite live region announces "Slide N of M" on transition.</li>
 *   <li>{@code prefers-reduced-motion}: disables autoplay AND swaps
 *       slide transition from {@code translateX} to opacity fade.</li>
 *   <li>Pauses autoplay on hover, focus-within, or tab hidden.</li>
 *   <li>Inactive slides get {@code inert} + {@code aria-hidden} so
 *       screen readers and Tab navigation skip them.</li>
 * </ul>
 *
 * <h3>Why a separate block from HeroSlider</h3>
 * {@code HeroSlider} renders a horizontal scroll-snap rail (multiple
 * slides visible at once, native scroll). This one shows ONE slide at
 * a time with explicit controls. They look + behave entirely
 * differently; conflating them would clutter both schemas.
 */
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type TouchEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  PortalLink,
  isVisible,
  useCurrentUser,
  useBlockPart,
  type CurrentUser,
} from '@akademi/site-builder-runtime';
import { Button } from '@akademi/ui';
import { resizeImageUrl } from '../_shared/resizeImageUrl';
import { resolveInsetStyle, type PaddingBox } from '../_shared/resolvePadding';
import type { CampaignAudience } from '../_shared/campaignAudience';
import { HeroEmptyFallback } from '../_shared/HeroEmptyFallback';
import { BlockRoot } from '../_shared/BlockRoot';

export interface HeroCarouselSlide extends CampaignAudience {
  kicker?: string;
  title?: string;
  subtitle?: string;
  image?: string;
  /** Alt text for the background image. Falls back to {@code title}. */
  imageAlt?: string;
  ctaLabel?: string;
  ctaHref?: string;
  ctaSecondaryLabel?: string;
  ctaSecondaryHref?: string;
}

export interface HeroCarouselProps {
  slides?: HeroCarouselSlide[];
  /** Section height. {@code viewport} = 100dvh capped to a max.
   *  {@code auto} sizes the box to the slide image's real aspect ratio
   *  so a pre-designed banner graphic (logo/copy baked into the image)
   *  is never cropped by {@code object-fit: cover} at any width. */
  height?: 'auto' | 'xs' | 'sm' | 'md' | 'lg' | 'viewport';
  textAlign?: 'left' | 'center' | 'right';
  /** Overlay tint over the image. RGB only; alpha via
   *  {@link overlayOpacity}. */
  overlayColor?: string;
  /** 0..1 (clamped). 0 = image untouched, 1 = solid overlay. */
  overlayOpacity?: number;
  border?: 'none' | 'top' | 'bottom' | 'both' | 'all';
  borderColor?: string;
  radius?: 'none' | 'sm' | 'md' | 'lg';
  /** {@code fade} is gentler; {@code slide} is what people expect from
   *  a "carousel". */
  transition?: 'slide' | 'fade';
  /** Honoured only with 2+ slides AND no reduced-motion. */
  autoplay?: boolean;
  /** Min 2000 enforced at render. */
  autoplayDelay?: number;
  /** Default true. Ignored when {@code slides.length < 2}. */
  showArrows?: boolean;
  /** Default true. Ignored when {@code slides.length < 2}. */
  showDots?: boolean;
  /** 'full' (default) lets slide content span edge-to-edge; 'theme'
   *  constrains it to the page's max-width container. */
  maxWidth?: 'full' | 'theme';
  /** Space-token keys for padding between the section's true edge and
   *  the carousel (slides + arrows + dots) — the section's own
   *  background shows through this gap, framing the carousel. `radius`
   *  applies to both the section's outer edge and this inset frame. */
  paddingOuter?: PaddingBox;
}

const HEIGHT_MAP: Record<NonNullable<HeroCarouselProps['height']>, string> = {
  // `auto` sizes to the image's real aspect ratio; this value is only the
  // pre-load floor used before the first image reports its dimensions.
  auto: 'clamp(200px, 40dvh, 520px)',
  xs: 'clamp(120px, 18dvh, 200px)',
  sm: 'clamp(200px, 30dvh, 400px)',
  md: 'clamp(320px, 45dvh, 520px)',
  lg: 'clamp(400px, 60dvh, 640px)',
  viewport: 'min(100dvh, 900px)',
};

const RADIUS_MAP: Record<NonNullable<HeroCarouselProps['radius']>, string> = {
  none: '0px',
  sm: 'var(--radius-sm, 6px)',
  md: 'var(--radius-md, 12px)',
  lg: 'var(--radius-lg, 16px)',
};

const SWIPE_THRESHOLD = 50;
// Below this horizontal motion we treat the gesture as a vertical
// scroll and let the browser handle it. Prevents stealing scroll.
const HORIZONTAL_LOCK_RATIO = 1.2;

export function HeroCarousel(props: HeroCarouselProps) {
  // Support legacy saved data where x-advanced fields are nested under
  // _advanced instead of being flat — runtime should unwrap, but this
  // ensures the component is resilient to both shapes.
  const adv = (props as unknown as { _advanced?: Partial<HeroCarouselProps> })._advanced ?? {};

  // Whether the block author actually chose a height. Puck fills in the
  // schema default ('lg') for every new instance, so this is only ever
  // true for pre-existing content saved before the `height` field existed.
  const heightUnset = props.height == null;

  const {
    slides: rawSlides,
    height = 'lg',
    textAlign = 'left',
    border = 'none',
    radius = 'none',
    autoplay = false,
    showArrows = true,
    showDots = true,
    maxWidth = 'full',
    transition = adv.transition ?? 'slide',
    borderColor = adv.borderColor ?? 'rgba(255,255,255,0.12)',
    overlayColor = adv.overlayColor ?? '#000000',
    overlayOpacity = adv.overlayOpacity ?? 0.45,
    autoplayDelay = adv.autoplayDelay ?? 5000,
    paddingOuter,
  } = props;
  // CSS `padding` on the section would NOT inset the slides — each slide's
  // image is `position: absolute; inset: 0`, and an absolutely positioned
  // element's containing block is its ancestor's *padding box* (which
  // already includes the padding area), so plain padding has no visible
  // effect on it. This carries the gap as explicit inset offsets instead,
  // wrapping the whole carousel (slides + arrows + dots) so it reads as
  // framed within the section.
  const outerInset = resolveInsetStyle(paddingOuter);
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const currentUser = useCurrentUser<CurrentUser>();
  // Drop entirely-empty slides (no title, no image, no copy) defensively.
  // Half-authored entries would otherwise render as black rectangles.
  // Also drop slides whose campaign has expired or that don't target
  // the current viewer's dynamic/security groups.
  const slides = useMemo(() => {
    const list = Array.isArray(rawSlides) ? rawSlides : [];
    return list
      .filter((s) => s && (s.title || s.subtitle || s.kicker || s.image || s.ctaLabel))
      .filter((s) => isVisible(s, currentUser));
  }, [rawSlides, currentUser]);

  const count = slides.length;
  const isCarousel = count > 1;
  const uid = useId().replace(/:/g, '-');

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  // Natural width/height of each slide's image, keyed by slide index.
  // When no explicit `height` was ever chosen (see `heightUnset` above),
  // lets the section adopt the *real* image aspect ratio (via CSS
  // `aspect-ratio`) instead of a fixed dvh-based height, so
  // `object-fit: cover` never has to crop a designed banner (logo/copy
  // baked into the image) — the box always matches the image's shape
  // and just scales with available width (sidebar toggle, narrow
  // viewports, etc). Once a height is chosen, it always wins.
  const [naturalRatios, setNaturalRatios] = useState<Record<number, number>>({});
  const activeRatio = naturalRatios[index];
  const recordRatio = (i: number, img: HTMLImageElement) => {
    const { naturalWidth, naturalHeight } = img;
    if (!naturalWidth || !naturalHeight) return;
    const ratio = naturalWidth / naturalHeight;
    setNaturalRatios((prev) => (prev[i] === ratio ? prev : { ...prev, [i]: ratio }));
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReducedMotion(mq.matches);
    sync();
    mq.addEventListener?.('change', sync);
    return () => {
      mq.removeEventListener?.('change', sync);
    };
  }, []);

  // Page-visibility pause: don't auto-advance while the tab is hidden.
  // Avoids the "come back, the carousel cycled 14 times" effect.
  const [pageHidden, setPageHidden] = useState(false);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const sync = () => setPageHidden(document.hidden);
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => document.removeEventListener('visibilitychange', sync);
  }, []);

  // Autoplay tick — re-armed on every dep change so the timer never
  // drifts. No-op when only 1 slide, autoplay false, reduced motion,
  // user paused, or tab hidden.
  useEffect(() => {
    if (!isCarousel || !autoplay || reducedMotion || paused || pageHidden) return;
    const delay = Math.max(2000, autoplayDelay);
    const t = setTimeout(() => {
      setIndex((i) => (i + 1) % count);
    }, delay);
    return () => clearTimeout(t);
  }, [index, isCarousel, autoplay, reducedMotion, paused, pageHidden, autoplayDelay, count]);

  // Wrapped indices: prev from 0 → last, next from last → 0.
  const go = (next: number) => {
    if (!isCarousel) return;
    setIndex(((next % count) + count) % count);
  };
  const prev = () => go(index - 1);
  const next = () => go(index + 1);

  // -- Touch gestures ------------------------------------------------------
  //
  // Track start on touchstart, decide on touchend. We do NOT
  // preventDefault on touchmove — that would block vertical page
  // scrolling, worse UX than the rare ambiguous gesture. Filter via
  // a horizontal-motion ratio instead.

  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  const onTouchStart = (e: TouchEvent) => {
    if (!isCarousel) return;
    const t = e.touches[0];
    if (!t) return;
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  };

  const onTouchEnd = (e: TouchEvent) => {
    if (!isCarousel) return;
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dy) * HORIZONTAL_LOCK_RATIO > Math.abs(dx)) return;
    if (Math.abs(dx) < SWIPE_THRESHOLD) return;
    // Natural direction: swipe LEFT (dx < 0) advances.
    if (dx < 0) next();
    else prev();
  };

  // -- Keyboard nav --------------------------------------------------------

  const onKeyDown = (e: KeyboardEvent) => {
    if (!isCarousel) return;
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        prev();
        break;
      case 'ArrowRight':
        e.preventDefault();
        next();
        break;
      case 'Home':
        e.preventDefault();
        go(0);
        break;
      case 'End':
        e.preventDefault();
        go(count - 1);
        break;
    }
  };

  const sectionHeight =
    HEIGHT_MAP[height as keyof typeof HEIGHT_MAP] ?? (height as string) ?? HEIGHT_MAP['lg'];
  const sectionRadius = RADIUS_MAP[radius];

  if (count === 0) {
    return (
      <HeroEmptyFallback
        style={{ width: '100%', height: sectionHeight, borderRadius: sectionRadius }}
      />
    );
  }

  const borderStyle = `1px solid ${borderColor}`;
  const sectionBorders: CSSProperties =
    border === 'all'
      ? { border: borderStyle }
      : border === 'both'
        ? { borderTop: borderStyle, borderBottom: borderStyle }
        : border === 'top'
          ? { borderTop: borderStyle }
          : border === 'bottom'
            ? { borderBottom: borderStyle }
            : {};

  const clampedOpacity = Math.max(0, Math.min(1, overlayOpacity));

  return (
    <BlockRoot
      as="section"
      role={isCarousel ? 'region' : undefined}
      aria-roledescription={isCarousel ? 'carousel' : undefined}
      aria-label={isCarousel ? t('hero.carousel.featuredBanner') : undefined}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={onKeyDown}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      style={{
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        // Only fall back to ratio-based sizing when the author never
        // chose a height (legacy content predating the `height` field).
        // Once we know the image's real aspect ratio, size purely from
        // that — no maxHeight cap. Capping height while width keeps
        // growing (wide screens) would re-introduce the exact mismatch
        // this ratio-based sizing exists to avoid: `cover` cropping the
        // sides because the box no longer matches the image's shape.
        // A chosen height always wins, even once the ratio is known.
        // Aspect-ratio sizing when the author picked `auto` (or for legacy
        // content saved before the `height` field existed). The box adopts
        // the image's real shape and just scales with available width, so
        // `object-fit: cover` never crops a pre-designed banner — at any
        // viewport. Until the first image reports its dimensions we fall
        // back to the fixed height (a sensible pre-load floor). Any other
        // chosen height always wins, even once the ratio is known.
        ...(activeRatio && (height === 'auto' || heightUnset)
          ? { height: 'auto', aspectRatio: activeRatio }
          : { height: sectionHeight }),
        borderRadius: sectionRadius,
        background: 'var(--color-surface-card, #0a0a0a)',
        color: 'var(--color-text-primary, #fafafa)',
        outline: 'none',
        ...sectionBorders,
        // 'theme' boxes the whole carousel (background + text) into the
        // page's max-width column, matching the header's maxWidth toggle.
        ...(maxWidth === 'theme'
          ? {
              maxWidth: 'var(--sb-max-width-1200, var(--app-max-width, 1200px))',
              width: '100%',
              margin: '0 auto',
            }
          : {}),
      }}
      tabIndex={-1}
    >
      {/* Inset ring carries `paddingOuter` as explicit offsets (see the
       *  comment above `outerInset`) — everything visual (slides, arrows,
       *  dots, live region) nests inside so the whole carousel frames
       *  inward from the section's true edges. Its own `overflow: hidden`
       *  + `borderRadius` clip the slide track to *this* box (not the
       *  section's, which now sits further out once padding is set) — so
       *  the rounded corners land on the frame you actually see, not the
       *  invisible outer edge, and sliding transitions don't bleed into
       *  the padding gap. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          borderRadius: sectionRadius,
          ...outerInset,
        }}
      >
        {/* Slide track. For {@code slide} transition we lay every slide
         *  side by side and translate horizontally. For {@code fade}
         *  (and reduced motion regardless of the prop) each slide
         *  overlaps absolutely and we toggle opacity. */}
        <div
          style={
            transition === 'slide' && !reducedMotion
              ? {
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  width: `${count * 100}%`,
                  transform: `translateX(-${index * (100 / count)}%)`,
                  transition: 'transform 480ms cubic-bezier(0.4, 0.0, 0.2, 1)',
                }
              : { position: 'absolute', inset: 0 }
          }
        >
          {slides.map((slide, i) => {
            const active = i === index;
            const isFadeMode = transition === 'fade' || reducedMotion;
            // No primary button rendered (no ctaLabel) but a primary href was
            // still authored — make the image itself the link instead of
            // leaving that href unreachable. Once a button renders, the href
            // belongs to it alone; the image stays a plain, non-interactive
            // background.
            const imageIsLink = !slide.ctaLabel && !!slide.ctaHref;
            const image = slide.image && (
              <img
                src={resizeImageUrl(slide.image, '1600x0')}
                alt={slide.imageAlt || slide.title || ''}
                loading={i === 0 ? 'eager' : 'lazy'}
                fetchPriority={i === 0 ? 'high' : undefined}
                decoding="async"
                // Covers the slow-load case. For images already in the
                // browser cache (or `eager`-loaded before hydration
                // attaches listeners), `load` may never fire post-mount
                // — the ref callback below catches that via `.complete`.
                onLoad={(e) => recordRatio(i, e.currentTarget)}
                ref={(el) => {
                  if (el && el.complete) recordRatio(i, el);
                }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
                {...(slide.imageAlt === '' ? { role: 'presentation' } : {})}
              />
            );
            return (
              <article
                key={i}
                role={isCarousel ? 'group' : undefined}
                aria-roledescription={isCarousel ? 'slide' : undefined}
                aria-label={
                  isCarousel ? t('hero.carousel.slidePosition', { index: i + 1, count }) : undefined
                }
                aria-hidden={isCarousel && !active}
                // `inert` removes the subtree from tab order + screen
                // reader navigation so users can't tab into hidden
                // slides' CTAs.
                inert={isCarousel && !active}
                style={
                  isFadeMode
                    ? {
                        position: 'absolute',
                        inset: 0,
                        opacity: active ? 1 : 0,
                        transition: reducedMotion ? 'none' : 'opacity 360ms ease',
                        pointerEvents: active ? 'auto' : 'none',
                      }
                    : {
                        flex: `0 0 ${100 / count}%`,
                        position: 'relative',
                      }
                }
              >
                {imageIsLink ? (
                  <PortalLink
                    href={slide.ctaHref}
                    style={{ position: 'absolute', inset: 0, display: 'block' }}
                  >
                    {image}
                  </PortalLink>
                ) : (
                  image
                )}
                {slide.image && clampedOpacity > 0 && (
                  <div
                    aria-hidden
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: overlayColor,
                      opacity: clampedOpacity,
                      // Passes clicks through to the image link beneath it
                      // when the image is the link — this div is purely a
                      // decorative tint, never interactive itself.
                      pointerEvents: 'none',
                    }}
                  />
                )}
                {/* Content. Padding uses clamp() so narrow viewports
                 *  don't get oversized side padding. */}
                <div
                  style={{
                    position: 'relative',
                    zIndex: 1,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems:
                      textAlign === 'center'
                        ? 'center'
                        : textAlign === 'right'
                          ? 'flex-end'
                          : 'flex-start',
                    textAlign,
                    padding: 'clamp(var(--space-12, 48px), 5vw, var(--space-7, 56px))',
                    gap: 'clamp(var(--space-2, 8px), 1.5vw, var(--space-4, 16px))',
                    maxWidth: 'none',
                    margin: '0 auto',
                    width: '100%',
                    boxSizing: 'border-box',
                    // This box spans the slide's full height/width (it's how
                    // kicker/title/subtitle/CTAs get centered/aligned within
                    // it), which otherwise sits on top of — and swallows every
                    // click meant for — the image link below across its
                    // *entire* box, not just the visible text/buttons. Text
                    // isn't interactive anyway; only the CTA row opts back in.
                    pointerEvents: imageIsLink ? 'none' : undefined,
                  }}
                >
                  {slide.kicker && (
                    <span
                      {...part('kicker', i)}
                      style={{
                        display: 'inline-block',
                        padding: 'var(--space-1-5, 6px) var(--space-3-5, 14px)',
                        borderRadius: 'var(--radius-pill, 999px)',
                        background: 'rgba(0,0,0,0.6)',
                        backdropFilter: 'blur(6px)',
                        WebkitBackdropFilter: 'blur(6px)',
                        fontSize: 'clamp(11px, 1.1vw, 13px)',
                        fontWeight: 600,
                        letterSpacing: 'var(--font-tracking-wide, 0.02em)',
                        color: 'var(--color-text-on-brand, #ffffff)',
                        border: '1px solid rgba(255,255,255,0.08)',
                      }}
                    >
                      {slide.kicker}
                    </span>
                  )}
                  {slide.title && (
                    <h2
                      {...part('title', i)}
                      style={{
                        margin: 0,
                        fontSize: 'clamp(24px, 4vw, 44px)',
                        lineHeight: 1.15,
                        fontWeight: 700,
                        fontFamily: 'var(--font-family-display, inherit)',
                        color: 'var(--color-text-primary-inverse, #ffffff)',
                        // Slight shadow keeps the headline legible over
                        // busy imagery without needing a heavier overlay.
                        // textShadow: '0 1px 14px rgba(0,0,0,0.3)',
                        maxWidth: '720px',
                      }}
                    >
                      {slide.title}
                    </h2>
                  )}
                  {slide.subtitle && (
                    <p
                      {...part('subtitle', i)}
                      style={{
                        margin: 0,
                        fontSize: 'clamp(13px, 1.4vw, 17px)',
                        lineHeight: 1.5,
                        color: 'var(--color-text-primary-inverse, #ffffff)',
                        maxWidth: '640px',
                      }}
                    >
                      {slide.subtitle}
                    </p>
                  )}
                  {(slide.ctaLabel || slide.ctaSecondaryLabel) && (
                    <div
                      style={{
                        display: 'flex',
                        gap: 'var(--space-3, 12px)',
                        marginTop: 'clamp(8px, 1.5vw, 14px)',
                        flexWrap: 'wrap',
                        justifyContent:
                          textAlign === 'center'
                            ? 'center'
                            : textAlign === 'right'
                              ? 'flex-end'
                              : 'flex-start',
                        // Opts back in when the parent content box has
                        // pointer-events disabled to let the image link
                        // through (see imageIsLink above) — a secondary
                        // button can still render with no primary ctaLabel.
                        pointerEvents: 'auto',
                      }}
                    >
                      {slide.ctaLabel && (
                        <Button href={slide.ctaHref || '#'} {...part('cta', i)}>
                          {slide.ctaLabel}
                        </Button>
                      )}
                      {slide.ctaSecondaryLabel && (
                        <Button
                          href={slide.ctaSecondaryHref || '#'}
                          variant="outline"
                          {...part('cta-secondary', i)}
                          style={{
                            height: 'auto',
                            padding: 'var(--space-3, 12px) var(--space-5, 20px)',
                            fontWeight: 600,
                            fontSize: 'clamp(13px, 1.2vw, 15px)',
                            background: 'transparent',
                            color: 'rgba(255,255,255,0.95)',
                            borderColor: 'rgba(255,255,255,0.35)',
                          }}
                        >
                          {slide.ctaSecondaryLabel}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>

        {/* Carousel chrome — only when 2+ slides. */}
        {isCarousel && showArrows && (
          <>
            <CarouselArrow direction="prev" onClick={prev} label={t('hero.carousel.previous')} />
            <CarouselArrow direction="next" onClick={next} label={t('hero.carousel.next')} />
          </>
        )}

        {isCarousel && showDots && (
          <div
            role="tablist"
            aria-label={t('hero.carousel.selector')}
            {...part('tablist')}
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 'clamp(12px, 2vw, 20px)',
              display: 'flex',
              justifyContent: 'center',
              gap: 'var(--space-2, 8px)',
              zIndex: 2,
            }}
          >
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i === index}
                aria-label={t('hero.carousel.goToSlide', { index: i + 1 })}
                onClick={() => go(i)}
                {...part('tab', i)}
                style={{
                  // Hit area meets the 24x24 CSS px touch-target minimum even
                  // though the visual pill (below) stays small.
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: i === index ? 28 : 24,
                  height: 24,
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    display: 'block',
                    width: i === index ? 28 : 10,
                    height: 10,
                    borderRadius: 'var(--radius-pill, 999px)',
                    background: i === index ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.4)',
                    transition: 'width 220ms ease, background 220ms ease',
                  }}
                />
              </button>
            ))}
          </div>
        )}

        {/* Polite live region for assistive tech. */}
        {isCarousel && (
          <div
            aria-live="polite"
            aria-atomic="true"
            style={{
              position: 'absolute',
              width: 1,
              height: 1,
              padding: 0,
              margin: -1,
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              border: 0,
            }}
          >
            {t('hero.carousel.slidePosition', { index: index + 1, count })}
          </div>
        )}
      </div>

      {/* Per-instance focus-visible style. useId-derived class so two
       *  carousels on the same page don't share focus styles via a
       *  global stylesheet. */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
            [data-hc="${uid}"] button:focus-visible {
              outline: 2px solid rgba(255,255,255,0.85);
              outline-offset: 3px;
            }
          `,
        }}
      />
      <div data-hc={uid} style={{ display: 'none' }} />
    </BlockRoot>
  );
}

// =============================================================================
// CarouselArrow — extracted purely for clarity
// =============================================================================

function CarouselArrow({
  direction,
  onClick,
  label,
}: {
  direction: 'prev' | 'next';
  onClick: () => void;
  label: string;
}) {
  const part = useBlockPart();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      {...part('pager', direction === 'prev' ? 0 : 1)}
      style={{
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        [direction === 'prev' ? 'left' : 'right']: 'clamp(8px, 2vw, 20px)',
        width: 44,
        height: 44,
        borderRadius: '50%',
        border: '1px solid rgba(255,255,255,0.25)',
        background: 'rgba(0,0,0,0.35)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        color: 'rgba(255,255,255,0.95)',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
        transition: 'background 150ms ease, transform 150ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(0,0,0,0.55)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(0,0,0,0.35)';
      }}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {direction === 'prev' ? (
          <polyline points="15 18 9 12 15 6" />
        ) : (
          <polyline points="9 18 15 12 9 6" />
        )}
      </svg>
    </button>
  );
}
